import { CSSProperties, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { KratosTokenKey, KratosTokens, PrimordialStats, SimulationMode, WeaponCompareSettings, WeaponMatrixTarget } from '../core/types';
import { Utils } from '../core/utilities';
import { Constants } from '../core/constants';
import primordialData from '../core/constants/primordials.json';
import { getATRangeLabel, getAdjustedATValues, getRepresentativeATValue } from '../core/utilities/primordial-at';
import { getKratosTokenPresentation, getVisibleKratosTokenKeys } from '../core/utilities/token-presentation';
import { loadBattlefieldPreferences, saveBattlefieldPreferences } from '../core/utilities/ui-storage';
import NumericField from './NumericField';
import SearchableSelect from './SearchableSelect';
import Button from './Button';
import SegmentedControl from './SegmentedControl';

interface PrimordialLevel {
    level: number;
    toHit: number;
    atBonus: number;
}

interface PrimordialCard {
    name: string;
    cycle: string;
    levels: PrimordialLevel[];
}

type CompareTarget = WeaponMatrixTarget & { hasBPData: boolean };

const allPrimordials = primordialData as PrimordialCard[];
const ROMAN_NUMERALS: Record<number, string> = { 0: '0', 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X' };
const BP_LEVELS = ['I', 'II', 'III'] as const;
const COMPARE_TOKEN_DEPTH = 10;

function dedupePrimordialCards(cards: PrimordialCard[]): PrimordialCard[] {
    const cardsByName = new Map<string, PrimordialCard>();

    for (const card of cards) {
        const existing = cardsByName.get(card.name);
        if (!existing || card.levels.length > existing.levels.length) {
            cardsByName.set(card.name, card);
        }
    }

    return Array.from(cardsByName.values());
}

function splitIntoTwoBalancedRows<T>(fields: T[]): T[][] {
    if (fields.length <= 4) {
        return [fields];
    }

    const firstRowLength = Math.ceil(fields.length / 2);
    return [fields.slice(0, firstRowLength), fields.slice(firstRowLength)];
}

function getToHitAtLevel(primordial: PrimordialCard, level: number): number {
    return primordial.levels.find((entry) => entry.level === level)?.toHit ?? 0;
}

function getATBonusAtLevel(primordial: PrimordialCard, level: number): number {
    return primordial.levels.find((entry) => entry.level === level)?.atBonus ?? 0;
}

function getVisibleTokenOptions(visibleTags: string[]): { value: KratosTokenKey; label: string }[] {
    return getVisibleKratosTokenKeys(visibleTags).map((value) => ({
        value,
        label: getKratosTokenPresentation(value).label,
    }));
}

function getCompareTargets(primordial: PrimordialCard | undefined, level: number): CompareTarget[] {
    if (!primordial) {
        return BP_LEVELS.map((bpLevel) => ({ bpLevel, aeonTrespassValue: 1, aeonTrespassValues: undefined, hasBPData: false }));
    }

    const atBonus = getATBonusAtLevel(primordial, level);
    return BP_LEVELS.map((bpLevel) => {
        const bpAtValues = getAdjustedATValues(primordial.name, bpLevel, atBonus);
        const representativeATValue = getRepresentativeATValue(bpAtValues);
        return {
            bpLevel,
            aeonTrespassValue: representativeATValue ?? 1,
            aeonTrespassValues: bpAtValues.length > 0 ? bpAtValues : undefined,
            hasBPData: bpAtValues.length > 0,
        };
    });
}

interface BattlefieldSectionProps {
    primordial: PrimordialStats;
    setPrimordial: React.Dispatch<React.SetStateAction<PrimordialStats>>;
    kratosPool: KratosTokens;
    visibleTags: string[];
    setKratosPool: React.Dispatch<React.SetStateAction<KratosTokens>>;
    simulationMode: SimulationMode;
    onCompareSettingsChange: (settings: WeaponCompareSettings) => void;
}

export const BattlefieldSection: React.FC<BattlefieldSectionProps> = ({
    primordial,
    setPrimordial,
    kratosPool,
    visibleTags,
    setKratosPool,
    simulationMode,
    onCompareSettingsChange,
}) => {
    const initialBattlefieldPreferences = useMemo(() => loadBattlefieldPreferences(), []);
    const [useCalculator, setUseCalculator] = useState(() => initialBattlefieldPreferences.primordialUseCalculator ?? true);

    const visiblePrimordials = useMemo(() => {
        const filtered = allPrimordials.filter((primordialCard) => visibleTags.includes(primordialCard.cycle));
        return dedupePrimordialCards(filtered.length > 0 ? filtered : allPrimordials).sort((a, b) => a.name.localeCompare(b.name));
    }, [visibleTags]);

    const [selectedPrimordialName, setSelectedPrimordialName] = useState(
        () => initialBattlefieldPreferences.selectedPrimordialName ?? visiblePrimordials[0]?.name ?? allPrimordials[0]?.name ?? '',
    );
    const selectedPrimordial = visiblePrimordials.find((primordialCard) => primordialCard.name === selectedPrimordialName) ?? visiblePrimordials[0] ?? allPrimordials[0];
    const availableLevels = useMemo(() => selectedPrimordial?.levels.map((level) => level.level).sort((a, b) => a - b) ?? [1], [selectedPrimordial]);
    const [selectedLevel, setSelectedLevel] = useState(() => {
        const storedLevel = initialBattlefieldPreferences.selectedPrimordialLevel;
        return typeof storedLevel === 'number' ? storedLevel : availableLevels.includes(1) ? 1 : availableLevels[0];
    });
    const [selectedBPLevel, setSelectedBPLevel] = useState<(typeof BP_LEVELS)[number]>(() => {
        const storedBPLevel = initialBattlefieldPreferences.selectedBPLevel;
        return storedBPLevel && BP_LEVELS.includes(storedBPLevel) ? storedBPLevel : 'I';
    });
    const visibleTokenOptions = useMemo(() => getVisibleTokenOptions(visibleTags), [visibleTags]);
    const [selectedTokens, setSelectedTokens] = useState<KratosTokenKey[]>(() => {
        const storedTokens = initialBattlefieldPreferences.compareSelectedTokens;
        return Array.isArray(storedTokens) ? storedTokens : visibleTokenOptions.map((option) => option.value);
    });
    const compareMode = simulationMode === 'compare';
    const showCalculator = compareMode || useCalculator;

    useEffect(() => {
        if (!selectedPrimordial || !visiblePrimordials.some((primordialCard) => primordialCard.name === selectedPrimordial.name)) {
            const nextPrimordial = visiblePrimordials[0] ?? allPrimordials[0];
            if (!nextPrimordial) return;
            setSelectedPrimordialName(nextPrimordial.name);
            const nextLevels = nextPrimordial.levels.map((level) => level.level).sort((a, b) => a - b);
            setSelectedLevel(nextLevels.includes(1) ? 1 : nextLevels[0]);
        }
    }, [selectedPrimordial, visiblePrimordials]);

    useEffect(() => {
        if (!availableLevels.includes(selectedLevel)) {
            setSelectedLevel(availableLevels.includes(1) ? 1 : availableLevels[0]);
        }
    }, [availableLevels, selectedLevel]);

    useEffect(() => {
        saveBattlefieldPreferences({
            primordialUseCalculator: useCalculator,
            selectedPrimordialName,
            selectedPrimordialLevel: selectedLevel,
            selectedBPLevel,
            compareSelectedTokens: selectedTokens,
        });
    }, [selectedBPLevel, selectedLevel, selectedPrimordialName, selectedTokens, useCalculator]);

    useEffect(() => {
        setSelectedTokens((previous) => {
            const visibleTokenValues = visibleTokenOptions.map((option) => option.value);
            const filtered = previous.filter((token) => visibleTokenValues.includes(token));
            return filtered.length > 0 ? filtered : visibleTokenValues;
        });
    }, [visibleTokenOptions]);

    const compareTargets = useMemo(() => getCompareTargets(selectedPrimordial, selectedLevel), [selectedPrimordial, selectedLevel]);
    const selectedVisibleTokens = useMemo(
        () => selectedTokens.filter((token) => visibleTokenOptions.some((option) => option.value === token)),
        [selectedTokens, visibleTokenOptions],
    );
    const hasAllBPData = compareTargets.every((target) => target.hasBPData);
    const calcToHit = selectedPrimordial ? getToHitAtLevel(selectedPrimordial, selectedLevel) : null;
    const selectedAdjustedATValues = useMemo(() => {
        if (!selectedPrimordial) return [];
        return getAdjustedATValues(selectedPrimordial.name, selectedBPLevel, getATBonusAtLevel(selectedPrimordial, selectedLevel));
    }, [selectedBPLevel, selectedLevel, selectedPrimordial]);
    const calcTotalAT = getRepresentativeATValue(selectedAdjustedATValues);
    const calcTotalATLabel = getATRangeLabel(selectedAdjustedATValues);

    const applyCalculatorPrimordial = useCallback(
        (bpLevel: (typeof BP_LEVELS)[number], primordialCard: PrimordialCard = selectedPrimordial, level: number = selectedLevel) => {
            if (!primordialCard) return;
            const toHit = getToHitAtLevel(primordialCard, level);
            const aeonTrespassValues = getAdjustedATValues(primordialCard.name, bpLevel, getATBonusAtLevel(primordialCard, level));
            const aeonTrespassValue = getRepresentativeATValue(aeonTrespassValues);

            setPrimordial((previous) => ({
                ...previous,
                toHitValue: toHit,
                aeonTrespassValue: aeonTrespassValue ?? previous.aeonTrespassValue,
                aeonTrespassValues: aeonTrespassValues.length > 0 ? aeonTrespassValues : undefined,
            }));
        },
        [selectedLevel, selectedPrimordial, setPrimordial],
    );

    useEffect(() => {
        if (!selectedPrimordial) return;
        onCompareSettingsChange({
            primordialName: selectedPrimordial.name,
            primordialLevel: selectedLevel,
            toHitValue: calcToHit ?? primordial.toHitValue,
            targets: compareTargets.map(({ bpLevel, aeonTrespassValue, aeonTrespassValues }) => ({ bpLevel, aeonTrespassValue, aeonTrespassValues })),
            tokenDepth: COMPARE_TOKEN_DEPTH,
            tokens: selectedVisibleTokens,
            hasAllBPData,
        });
    }, [calcToHit, compareTargets, hasAllBPData, onCompareSettingsChange, primordial.toHitValue, selectedPrimordial, selectedLevel, selectedVisibleTokens]);

    useEffect(() => {
        if (!compareMode && showCalculator && selectedPrimordial) {
            applyCalculatorPrimordial(selectedBPLevel);
        }
    }, [applyCalculatorPrimordial, compareMode, selectedBPLevel, selectedPrimordial, showCalculator]);

    const handleCalcPrimordialChange = (name: string) => {
        const nextPrimordial = visiblePrimordials.find((primordialCard) => primordialCard.name === name) ?? visiblePrimordials[0] ?? allPrimordials[0];
        const nextLevels = nextPrimordial?.levels.map((level) => level.level).sort((a, b) => a - b) ?? [1];
        const nextLevel = nextLevels.includes(1) ? 1 : nextLevels[0];
        setSelectedPrimordialName(nextPrimordial.name);
        setSelectedLevel(nextLevel);

        if (!compareMode && nextPrimordial) {
            applyCalculatorPrimordial(selectedBPLevel, nextPrimordial, nextLevel);
        }
    };

    const handleLevelChange = (level: number) => {
        setSelectedLevel(level);
        if (!compareMode && selectedPrimordial) {
            applyCalculatorPrimordial(selectedBPLevel, selectedPrimordial, level);
        }
    };

    const handleBPLevelChange = (bpLevel: (typeof BP_LEVELS)[number]) => {
        setSelectedBPLevel(bpLevel);
        if (selectedPrimordial) {
            applyCalculatorPrimordial(bpLevel);
        }
    };

    const handleToggleCalculator = (enabled: boolean) => {
        if (compareMode) return;
        setUseCalculator(enabled);
        if (enabled && selectedPrimordial) {
            applyCalculatorPrimordial(selectedBPLevel);
        } else if (!enabled) {
            setPrimordial((previous) => (previous.aeonTrespassValues ? { ...previous, aeonTrespassValues: undefined } : previous));
        }
    };

    const handlePrimordialIncrement = (field: 'toHitValue' | 'aeonTrespassValue', increment: number) => {
        handlePrimordialChange(field, (primordial[field] ?? 0) + increment);
    };

    const handlePrimordialChange = useCallback(
        (field: 'toHitValue' | 'aeonTrespassValue', value: number) => {
            value = Math.max(value, 1);
            value = Math.min(value, 99);
            setPrimordial((previous) => {
                const nextPrimordial = { ...previous, [field]: value, aeonTrespassValues: undefined };
                return previous[field] !== value || previous.aeonTrespassValues ? nextPrimordial : previous;
            });
        },
        [setPrimordial],
    );

    const handleKratosIncrement = (field: keyof KratosTokens, increment: number) => {
        handleKratosChange(field, (kratosPool[field] ?? 0) + increment);
    };

    const handleKratosChange = useCallback(
        (token: keyof KratosTokens, value: number) => {
            value = Math.max(value, 0);
            value = Math.min(value, 99);
            setKratosPool((previous) => (previous[token] !== value ? { ...previous, [token]: value } : previous));
        },
        [setKratosPool],
    );

    const handleTokenToggle = (token: KratosTokenKey) => {
        setSelectedTokens((previous) => (previous.includes(token) ? previous.filter((selected) => selected !== token) : [...previous, token]));
    };

    const renderField = (label: string, value: number, onIncrement: () => void, onDecrement: () => void, tokenKey?: KratosTokenKey) => (
        <NumericField label={label.replace(':', '')} value={value} onDecrement={onDecrement} onIncrement={onIncrement} tokenKey={tokenKey} />
    );

    return (
        <>
            <div className="input-section battlefield-section battlefield-panel">
                <div className="bf-primordial-header">
                    <h3>Primordial</h3>
                    {!compareMode && (
                        <SegmentedControl
                            ariaLabel="Primordial Input Mode"
                            className="bf-mode-toggle"
                            size="sm"
                            value={useCalculator ? 'calculated' : 'manual'}
                            onChange={(nextValue) => handleToggleCalculator(nextValue === 'calculated')}
                            options={[
                                { value: 'calculated', label: 'Calculated' },
                                { value: 'manual', label: 'Manual' },
                            ]}
                        />
                    )}
                </div>

                {showCalculator ? (
                    <div className="bf-calculator">
                        <div className="bf-calc-row">
                            <label className="bf-label">Primordial:</label>
                            <SearchableSelect
                                value={selectedPrimordial?.name ?? ''}
                                options={visiblePrimordials.map((primordialCard) => ({ value: primordialCard.name }))}
                                onChange={handleCalcPrimordialChange}
                                ariaLabel="Primordial"
                                className="bf-searchable-select"
                            />
                        </div>
                        <div className="bf-calc-row">
                            <label className="bf-label">Level:</label>
                            <SegmentedControl
                                ariaLabel="Primordial Level"
                                className="bf-level-toggle"
                                size="sm"
                                value={selectedLevel}
                                onChange={handleLevelChange}
                                options={availableLevels.map((level) => ({
                                    value: level,
                                    label: ROMAN_NUMERALS[level] ?? level,
                                    ariaLabel: `Level ${level}`,
                                }))}
                            />
                        </div>
                        {compareMode ? null : (
                            <div className="bf-calc-row">
                                <label className="bf-label">BP Level:</label>
                                <SegmentedControl
                                    ariaLabel="BP Level"
                                    className="bf-bp-toggle"
                                    size="sm"
                                    value={selectedBPLevel}
                                    onChange={handleBPLevelChange}
                                    options={BP_LEVELS.map((bpLevel) => ({
                                        value: bpLevel,
                                        label: bpLevel,
                                        ariaLabel: `BP ${bpLevel}`,
                                    }))}
                                />
                            </div>
                        )}

                        {compareMode ? (
                            <>
                                <p className="bf-compare-copy">Uses the selected primordial&apos;s BP I, BP II, and BP III values.</p>
                                <div className="bf-calc-results bf-calc-results-compare">
                                    <div className="bf-calc-result">
                                        <span className="bf-calc-result-label">To-Hit</span>
                                        <span className="bf-calc-result-value">{calcToHit ?? '—'}</span>
                                    </div>
                                    {compareTargets.map((target) => (
                                        <div key={target.bpLevel} className="bf-calc-result">
                                            <span className="bf-calc-result-label">BP {target.bpLevel} A.T.</span>
                                            <span className="bf-calc-result-value">{target.hasBPData ? (getATRangeLabel(target.aeonTrespassValues ?? []) ?? target.aeonTrespassValue) : '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="bf-calc-results">
                                <div className="bf-calc-result">
                                    <span className="bf-calc-result-label">To-Hit</span>
                                    <span className="bf-calc-result-value">{calcToHit ?? '—'}</span>
                                </div>
                                <div className="bf-calc-result">
                                    <span className="bf-calc-result-label">A.T. Range</span>
                                    <span className="bf-calc-result-value">{calcTotalATLabel ?? (calcTotalAT ?? '—')}</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="bf-row">
                            {renderField('To-Hit Value:', primordial.toHitValue, () => handlePrimordialIncrement('toHitValue', 1), () => handlePrimordialIncrement('toHitValue', -1))}
                            {renderField('A.T. Value:', primordial.aeonTrespassValue, () => handlePrimordialIncrement('aeonTrespassValue', 1), () => handlePrimordialIncrement('aeonTrespassValue', -1))}
                        </div>
                        <div className="bf-reset-row">
                            <Button className="reset-button" variant="danger" fullWidth onClick={() => setPrimordial({ toHitValue: 7, aeonTrespassValue: 3, aeonTrespassValues: undefined })}>
                                Reset Primordial
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <div className="input-section battlefield-section battlefield-panel">
                <div className="bf-section-header">
                    <h3>{compareMode ? 'Kratos Token Impact' : 'Kratos Pool'}</h3>
                </div>
                {compareMode ? (
                    <>
                        <p className="bf-compare-copy">Choose which tokens to sweep from +0 through +10.</p>
                        <div className="bf-token-options">
                            {splitIntoTwoBalancedRows(visibleTokenOptions).map((row, rowIndex) => (
                                <div key={rowIndex} className="bf-token-options-row" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
                                    {row.map((option) => (
                                        <label
                                            key={option.value}
                                            style={
                                                {
                                                    '--token-accent-color': getKratosTokenPresentation(option.value).color,
                                                    '--token-accent-tint': getKratosTokenPresentation(option.value).tint,
                                                } as CSSProperties
                                            }
                                        >
                                            <input type="checkbox" checked={selectedVisibleTokens.includes(option.value)} onChange={() => handleTokenToggle(option.value)} />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                            ))}
                        </div>
                        {!hasAllBPData && <div className="bf-warning">This primordial is missing BP data, so weapon compare cannot run yet.</div>}
                        {selectedVisibleTokens.length === 0 && <div className="bf-warning">Select at least one token to include in the comparison.</div>}
                    </>
                ) : (
                    <>
                        {(() => {
                            type TokenDef = { label: string; key: keyof KratosTokens };
                            const tokens: TokenDef[] = getVisibleKratosTokenKeys(visibleTags).map((key) => ({
                                label: `${getKratosTokenPresentation(key).label}:`,
                                key,
                            }));
                            const rows: TokenDef[][] = [];
                            for (let index = 0; index < tokens.length; index += 2) {
                                rows.push(tokens.slice(index, index + 2));
                            }
                            return rows.map((pair, index) => (
                                <div key={index} className="bf-row">
                                    {pair.map(({ label, key }) => (
                                        <Fragment key={key}>{renderField(label, kratosPool[key] || 0, () => handleKratosIncrement(key, 1), () => handleKratosIncrement(key, -1), key)}</Fragment>
                                    ))}
                                    {pair.length === 1 && <div className="bf-field" />}
                                </div>
                            ));
                        })()}
                        <div className="bf-reset-row">
                            <Button className="reset-button" variant="danger" fullWidth onClick={() => setKratosPool(Utils.shallowCopy(Constants.EMPTY_KRATOS_POOL))}>
                                Reset Kratos Pool
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};
